import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import {
  TrendingUp, Mail, ArrowRight, Shield, CheckCircle,
  BarChart3, PoundSterling, Loader2, AlertCircle, Lock, Eye, EyeOff, User
} from "lucide-react";

const VALUE_PROPS = [
  { icon: BarChart3, text: "50/30/20 budget planner with live visualisation" },
  { icon: TrendingUp, text: "Net worth tracker across ISA, pension & assets" },
  { icon: PoundSterling, text: "UK-specific calculators with real HMRC tax rates" },
  { icon: Shield, text: "Your data is private, encrypted, and yours alone" },
];

export default function Signup() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const validateForm = () => {
    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters long.");
      return false;
    }
    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || !confirmPassword) return;
    
    if (!validateForm()) {
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: password,
        options: {
          data: {
            full_name: fullName.trim(),
          },
        },
      });

      if (error) {
        const errMsg = error.message || "";
        // Check for rate limiting or body stream errors
        if (
          error.status === 429 ||
          errMsg.includes("rate") ||
          errMsg.includes("body stream") ||
          errMsg.includes("already read") ||
          errMsg.includes("already used")
        ) {
          setErrorMsg("Too many requests. Please wait 60 seconds and try again.");
        } else if (errMsg.includes("already registered")) {
          setErrorMsg("This email is already registered. Please sign in instead.");
        } else {
          setErrorMsg(errMsg || "Something went wrong. Please try again.");
        }
        setStatus("error");
      } else if (data?.user) {
        // Check if email confirmation is required
        if (data.user.identities?.length === 0) {
          setErrorMsg("This email is already registered. Please sign in instead.");
          setStatus("error");
        } else if (data.session) {
          // Auto-confirmed, redirect to dashboard
          navigate("/dashboard");
        } else {
          // Email confirmation required
          setStatus("success");
        }
      }
    } catch (err) {
      console.error("Signup error:", err);
      const errMsg = err?.message || String(err);
      
      // Handle body stream errors (caused by rate limiting)
      if (errMsg.includes("body stream") || errMsg.includes("already read") || errMsg.includes("already used")) {
        setErrorMsg("Too many requests. Please wait 60 seconds and try again.");
      } else {
        setErrorMsg("Network error. Please check your connection and try again.");
      }
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="signup-page">
      {/* ── Left Panel (branding) ── */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between relative overflow-hidden bg-slate-900/60 border-r border-white/5 p-12">
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 30% -10%, rgba(16,185,129,0.12), transparent 60%)",
          }}
        />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.025]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <TrendingUp className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-outfit font-bold text-white text-lg leading-tight">Kara Finance</p>
            <p className="text-emerald-400 text-[10px] font-medium uppercase tracking-widest">Budget Pro</p>
          </div>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="font-outfit font-bold text-4xl xl:text-5xl text-white leading-[1.1] tracking-tight mb-4">
              Start Building
              <br />
              <span className="text-emerald-400">Your Wealth Today</span>
            </h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-sm">
              Join thousands of UK investors using Kara Finance to track, plan, and grow their finances.
            </p>
          </div>

          {/* Value props */}
          <ul className="space-y-4">
            {VALUE_PROPS.map((vp, i) => {
              const Icon = vp.icon;
              return (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{vp.text}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer note */}
        <p className="relative z-10 text-slate-700 text-xs">
          Not financial advice. For informational purposes only.
        </p>
      </div>

      {/* ── Right Panel (form) ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12 py-12">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-10">
          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
          </div>
          <p className="font-outfit font-bold text-white text-lg">Kara Finance Budget Pro</p>
        </div>

        <div className="w-full max-w-sm">
          {status === "success" ? (
            /* ── Success State ── */
            <div data-testid="signup-success" className="text-center space-y-5">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h2 className="font-outfit font-bold text-2xl text-white mb-2">Check your email!</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  We sent a confirmation link to
                </p>
                <p className="text-emerald-400 font-semibold text-sm mt-0.5 break-all">{email}</p>
              </div>
              <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4 text-left space-y-2">
                <p className="text-slate-400 text-xs">
                  <span className="text-slate-300 font-medium">1.</span> Open the email from Supabase
                </p>
                <p className="text-slate-400 text-xs">
                  <span className="text-slate-300 font-medium">2.</span> Click the confirmation link
                </p>
                <p className="text-slate-400 text-xs">
                  <span className="text-slate-300 font-medium">3.</span> Return here to sign in
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
              >
                Go to Sign In
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            /* ── Form State ── */
            <div data-testid="signup-form">
              <div className="mb-8">
                <h2 className="font-outfit font-bold text-2xl text-white mb-2">
                  Create your account
                </h2>
                <p className="text-slate-400 text-sm">
                  Start your journey to financial freedom.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-1.5">
                    Full name <span className="text-slate-600">(optional)</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="text"
                      data-testid="fullname-input"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-600 text-sm outline-none transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="email"
                      data-testid="email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoFocus
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-600 text-sm outline-none transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type={showPassword ? "text" : "password"}
                      data-testid="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 rounded-xl pl-10 pr-12 py-3 text-white placeholder-slate-600 text-sm outline-none transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-slate-600 text-xs mt-1">Minimum 6 characters</p>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-1.5">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type={showPassword ? "text" : "password"}
                      data-testid="confirm-password-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-600 text-sm outline-none transition-all duration-200"
                    />
                  </div>
                </div>

                {status === "error" && (
                  <div data-testid="signup-error" className="flex items-center gap-2 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <p className="text-rose-400 text-xs">{errorMsg}</p>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="signup-button"
                  disabled={status === "loading" || !email.trim() || !password || !confirmPassword}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm rounded-xl py-3 transition-all duration-200 hover:shadow-[0_0_24px_rgba(16,185,129,0.3)] hover:-translate-y-0.5"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-400 text-sm">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                  >
                    Sign in
                  </Link>
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-white/5">
                <p className="text-slate-600 text-xs text-center">
                  By creating an account, you agree that this is a personal finance tool for informational purposes only.
                </p>
              </div>

              <div className="mt-4 text-center">
                <Link
                  to="/"
                  className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
                >
                  ← Back to Kara Finance
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
